import { backendApi, uploadApi } from "./axios";
import { PAPERS_ENDPOINTS } from "./endpoints";

// Query params are built with URLSearchParams so array filters repeat the key
// (?year=2023&year=2022), which is what the backend expects.
function buildParams(filters = {}) {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") return;
        if (Array.isArray(value)) {
            value.forEach((entry) => {
                if (entry !== null && entry !== undefined && entry !== "") params.append(key, entry);
            });
        } else {
            params.append(key, value);
        }
    });

    return params;
}

/**
 * Filtered, paginated list for students, tutors and institutes.
 * @param {Object} filters grade, subject, year, medium, term, searchTerm, page, limit, and
 *                         hasMarkingScheme / hasAnswerSheet for tutors.
 */
export function listPapers(filters = {}) {
    return backendApi.get(PAPERS_ENDPOINTS.FETCH, { params: buildParams(filters) });
}

/**
 * Filter dropdown options, counted from the papers that exist. Scoped by grade.
 */
export function fetchPaperFilters(filters = {}) {
    return backendApi.get(PAPERS_ENDPOINTS.FILTERS, { params: buildParams(filters) });
}

/**
 * Papers matching per-grade subject selections, grouped by grade.
 */
export function listPapersByGrade(filters, extraFilters = {}) {
    const params = buildParams(extraFilters);
    params.append("filters", JSON.stringify(filters));
    return backendApi.get(PAPERS_ENDPOINTS.BY_GRADE, { params });
}

/**
 * Exchanges a fileId for a short-lived signed URL. The backend re-checks the caller's role here,
 * so a student who somehow holds an answer sheet's fileId still gets a 403.
 */
export async function getPaperFileDownloadUrl(paperId, fileId) {
    const res = await backendApi.get(
        PAPERS_ENDPOINTS.DOWNLOAD_FILE.replace(":paperId", paperId).replace(":fileId", fileId)
    );
    return res.data?.data;
}

/* Admin */

/**
 * Same filters as listPapers, plus status: 'all' to include archived papers.
 */
export function listPapersForAdmin(filters = {}) {
    return backendApi.get(PAPERS_ENDPOINTS.ADMIN_LIST, { params: buildParams(filters) });
}

/**
 * Creates a paper and uploads its files in one multipart request.
 * @param {FormData} formData name, grade, subject, year, medium, term, plus the file fields
 *                            paperFiles / markingSchemeFiles / answerSheetFiles.
 */
export function createPaper(formData) {
    return uploadApi.post(PAPERS_ENDPOINTS.ADMIN_CREATE, formData);
}

/**
 * Creates one paper per uploaded file, all sharing the grade/subject/year/medium/term in the form.
 * Each paper is named after its file. Question papers only.
 * @param {FormData} formData grade, subject, year, medium, term + paperFiles[]
 * @returns created[], failed[{ fileName, error }], summary — a partial failure is still a 201.
 */
export function bulkCreatePapers(formData) {
    return uploadApi.post(PAPERS_ENDPOINTS.ADMIN_BULK_CREATE, formData);
}

/**
 * Updates paper details. Every field is optional; only what is sent changes.
 * @param {Object} payload name, grade, subject, medium, year, term, status
 */
export function updatePaper(paperId, payload) {
    return backendApi.put(PAPERS_ENDPOINTS.ADMIN_UPDATE.replace(":id", paperId), payload);
}

export function deletePaper(paperId) {
    return backendApi.delete(PAPERS_ENDPOINTS.ADMIN_DELETE.replace(":id", paperId));
}

/**
 * Attaches files to a paper that already exists — how an answer sheet gets added to a paper
 * uploaded weeks ago.
 */
export function addPaperFiles(paperId, formData) {
    return uploadApi.post(PAPERS_ENDPOINTS.ADMIN_ADD_FILES.replace(":id", paperId), formData);
}

export function deletePaperFile(paperId, fileId) {
    return backendApi.delete(
        PAPERS_ENDPOINTS.ADMIN_DELETE_FILE.replace(":id", paperId).replace(":fileId", fileId)
    );
}
